import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ledgerApi, type LedgerQueryParams, type LedgerSummary } from "../lib/ledgerApi";

const PAGE_SIZE = 30;

export const LEDGER_KEYS = {
  all: ["ledger"] as const,
  list: (params: Omit<LedgerQueryParams, "limit" | "lastKey">) =>
    [...LEDGER_KEYS.all, "list", params] as const,
  summary: (params: Omit<LedgerQueryParams, "limit" | "lastKey">) =>
    [...LEDGER_KEYS.all, "summary", params] as const,
};

export function useLedgerInfinite(
  params: Omit<LedgerQueryParams, "limit" | "lastKey">,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: LEDGER_KEYS.list(params),
    queryFn: ({ pageParam }) =>
      ledgerApi.list({ ...params, limit: PAGE_SIZE, lastKey: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    enabled,
    staleTime: 0,
  });
}

export function useLedgerSummary(
  params: Omit<LedgerQueryParams, "limit" | "lastKey">,
  enabled = true,
): { data: LedgerSummary | undefined; isLoading: boolean; error: Error | null } {
  return useQuery({
    queryKey: LEDGER_KEYS.summary(params),
    queryFn: () => ledgerApi.summary(params),
    enabled,
    staleTime: 0,
  });
}

export function useCreateManualLedgerEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      businessId: string;
      eventDate: string;
      manualDirection: "INCOME" | "EXPENSE";
      amount: number;
      description: string;
    }) => ledgerApi.createManualEntry(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEDGER_KEYS.all });
    },
  });
}
